import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
// Import Stripe packages conditionally
// Elements and loadStripe will be imported only when needed
import { 
  getSubscriptionStatus, 
  createCheckoutSession, 
  cancelSubscription, 
  reactivateSubscription, 
  updatePaymentMethod,
  isIosDevice,
  convertTrialToSubscription
} from '../../services/subscriptionService';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { 
  Loader,
  Star, 
  CheckCircle, 
  CreditCard, 
  Calendar,
  Info,
  AlertTriangle,
  DollarSign
} from 'lucide-react';

// Don't load Stripe outside of component - move this inside
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Placeholder pricing information - should match your Stripe configuration
const SUBSCRIPTION_PRICE = {
  monthly: 2.50,
  currency: 'USD'
};

// Component formatting functions
const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

const SubscriptionPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingAction, setProcessingAction] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const { isDarkMode } = useDarkMode();
  const [isIos] = useState(isIosDevice());
  // Initialize stripePromise as null
  const [stripePromise, setStripePromise] = useState(null);

  // Styles based on dark/light mode
  const styles = {
    container: `max-w-4xl mx-auto px-4 py-8 ${isDarkMode ? 'text-white' : 'text-gray-900'}`,
    header: `text-2xl font-semibold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`,
    card: `rounded-[24px] border overflow-hidden transition-all duration-200 ${
      isDarkMode 
        ? 'bg-[#111111] border-gray-800 shadow-lg shadow-black/5' 
        : 'bg-white border-gray-100 shadow-sm'
    }`,
    cardHeader: `p-6 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`,
    cardTitle: `text-xl font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`,
    cardBody: `p-6`,
    primaryButton: `px-4 py-2.5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 ${
      isDarkMode
        ? 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30'
        : 'bg-emerald-600 text-white hover:bg-emerald-700'
    }`,
    secondaryButton: `px-4 py-2.5 rounded-xl border transition-all duration-300 flex items-center justify-center gap-2 ${
      isDarkMode
        ? 'bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800'
        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
    }`,
    dangerButton: `px-4 py-2.5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 ${
      isDarkMode
        ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
        : 'bg-red-600 text-white hover:bg-red-700'
    }`,
    alert: `p-4 mb-6 rounded-xl flex items-start gap-3 ${
      isDarkMode
        ? 'bg-amber-500/10 text-amber-300 border border-amber-400/20'
        : 'bg-amber-50 text-amber-800 border border-amber-100'
    }`,
    successAlert: `p-4 mb-6 rounded-xl flex items-start gap-3 ${
      isDarkMode
        ? 'bg-green-500/10 text-green-300 border border-green-400/20'
        : 'bg-green-50 text-green-800 border border-green-100'
    }`,
    errorAlert: `p-4 mb-6 rounded-xl flex items-start gap-3 ${
      isDarkMode
        ? 'bg-red-500/10 text-red-300 border border-red-400/20'
        : 'bg-red-50 text-red-800 border border-red-100'
    }`,
    infoText: `${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`
  };

  // Initialize Stripe only if not on iOS and when component mounts
  useEffect(() => {
    if (!isIos && !stripePromise) {
      const initializeStripe = async () => {
        try {
          // Dynamically import Stripe only when needed
          const { loadStripe } = await import('@stripe/stripe-js');
          const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
          setStripePromise(stripe);
        } catch (err) {
          console.error('Failed to load Stripe:', err);
        }
      };
      
      initializeStripe();
    }
  }, [isIos, stripePromise]);

  // Fetch subscription status on component mount
  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSubscriptionStatus();
      setSubscription(data);
    } catch (err) {
      setError('Failed to load subscription details. Please try again later.');
      console.error('Error fetching subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSubscription = async () => {
    // Don't proceed on iOS devices
    if (isIos) {
      setError('Subscription management is not available on iOS devices');
      return;
    }
    
    try {
      setProcessingAction(true);
      setActionMessage('Preparing checkout...');
      
      // If the user is on a trial, use the convertTrialToSubscription function
      if (subscription?.isTrial) {
        const result = await convertTrialToSubscription();
        if (!result.success) {
          throw new Error(result.error || 'Failed to convert trial to subscription');
        }
        // Redirect is handled by the service
      } else {
        // Otherwise, create a new checkout session
        const result = await createCheckoutSession();
        if (!result.success) {
          throw new Error(result.error || 'Failed to create checkout session');
        }
        // Redirect is handled by the service
      }
    } catch (err) {
      setError(err.message || 'Failed to start subscription process');
      setProcessingAction(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setProcessingAction(true);
      setActionMessage('Processing cancellation...');
      const result = await cancelSubscription();
      if (result.success) {
        setActionMessage('Subscription cancelled successfully');
        // Refresh subscription status
        await fetchSubscriptionStatus();
        setShowConfirmCancel(false);
      } else {
        throw new Error(result.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setProcessingAction(true);
      setActionMessage('Reactivating subscription...');
      const result = await reactivateSubscription();
      if (result.success) {
        setActionMessage('Subscription reactivated successfully');
        // Refresh subscription status
        await fetchSubscriptionStatus();
      } else {
        throw new Error(result.error || 'Failed to reactivate subscription');
      }
    } catch (err) {
      setError(err.message || 'Failed to reactivate subscription');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleUpdatePayment = async () => {
    try {
      setProcessingAction(true);
      setActionMessage('Opening payment portal...');
      const result = await updatePaymentMethod();
      if (!result.success) {
        throw new Error(result.error || 'Failed to open payment portal');
      }
      // Redirect is handled by the service
    } catch (err) {
      setError(err.message || 'Failed to update payment method');
      setProcessingAction(false);
    }
  };

  // If on iOS, show a message that subscription management is not available
  if (isIos) {
    return (
      <div className={styles.container}>
        <h1 className={styles.header}>TV App Subscription</h1>
        
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Subscription Management</h2>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.alert}>
              <Info className="w-5 h-5 mt-0.5" />
              <div>
                <p className="font-medium">Subscription management is not available on iOS devices</p>
                <p className="mt-1">
                  Please visit our website from a desktop or Android device to manage your TV app subscription.
                </p>
              </div>
            </div>
            
            <p className={`mt-4 ${styles.infoText}`}>
              The Ummahti TV app requires a subscription after the free trial period.
              Your subscription status will be shown here once activated.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <h1 className={styles.header}>TV App Subscription</h1>
        <div className="flex justify-center items-center py-12">
          <Loader className="text-emerald-500 w-6 h-6 animate-spin" />
          <span className="ml-3">Loading subscription details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.header}>TV App Subscription</h1>
      
      {error && (
        <div className={styles.errorAlert}>
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-medium">Error</p>
            <p className="mt-1">{error}</p>
          </div>
        </div>
      )}
      
      {actionMessage && (
        <div className={styles.successAlert}>
          <CheckCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-medium">Success</p>
            <p className="mt-1">{actionMessage}</p>
          </div>
        </div>
      )}

      {/* Subscription Status Card */}
      <div className={styles.card + ' mb-6'}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Subscription Status</h2>
        </div>
        <div className={styles.cardBody}>
          {/* No Subscription or Trial */}
          {subscription?.status === 'none' && (
            <div className={subscription.hasHadFreeTrial ? styles.alert : styles.infoText + ' mb-4'}>
              {subscription.hasHadFreeTrial ? (
                <>
                  <Info className="w-5 h-5 mt-0.5" />
                  <div>
                    <p className="font-medium">You've already used your free trial</p>
                    <p className="mt-1">
                      Subscribe now to access all premium features for your TV app.
                    </p>
                  </div>
                </>
              ) : (
                <p>
                  Link a TV device to start your 30-day free trial! Once your TV is linked,
                  you'll have full access to all premium features for 30 days.
                </p>
              )}
            </div>
          )}

          {/* Trial Status */}
          {subscription?.isTrial && (
            <div className={`${subscription.daysRemaining > 5 ? styles.successAlert : styles.alert} mb-4`}>
              <span className="mt-0.5">
                {subscription.daysRemaining > 5 ? (
                  <Star className="w-5 h-5" />
                ) : (
                  <AlertTriangle className="w-5 h-5" />
                )}
              </span>
              <div>
                <p className="font-medium">
                  {subscription.daysRemaining > 0 
                    ? `Free Trial (${subscription.daysRemaining} days remaining)` 
                    : 'Free Trial Expired'}
                </p>
                <p className="mt-1">
                  {subscription.daysRemaining > 0 
                    ? `Your free trial ends on ${formatDate(subscription.trialEnd)}` 
                    : 'Your free trial has ended. Subscribe to continue using the TV app.'}
                </p>
                {subscription.isCustomTrial && (
                  <p className="mt-1 text-sm">
                    <Star className="w-4 h-4 inline mr-1" />
                    This trial was activated when you linked your TV device.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Active Subscription */}
          {subscription?.status === 'active' && (
            <div className={styles.successAlert + ' mb-4'}>
              <CheckCircle className="w-5 h-5 mt-0.5" />
              <div>
                <p className="font-medium">Active Subscription</p>
                <p className="mt-1">
                  Your subscription is active. Next billing date: {formatDate(subscription.currentPeriodEnd)}
                </p>
              </div>
            </div>
          )}

          {subscription?.status === 'canceled' && (
            <div className={styles.alert + ' mb-4'}>
              <Info className="w-5 h-5 mt-0.5" />
              <div>
                <p className="font-medium">Subscription Cancelled</p>
                <p className="mt-1">
                  Your subscription has been cancelled and will end on {formatDate(subscription.currentPeriodEnd)}.
                  You can reactivate your subscription before this date.
                </p>
              </div>
            </div>
          )}

          {['past_due', 'unpaid', 'incomplete'].includes(subscription?.status) && (
            <div className={styles.errorAlert + ' mb-4'}>
              <AlertTriangle className="w-5 h-5 mt-0.5" />
              <div>
                <p className="font-medium">Payment Issue</p>
                <p className="mt-1">
                  There's an issue with your subscription payment. Please update your payment method.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <DollarSign className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
                <div>
                  <p className={`text-sm ${styles.infoText}`}>Monthly Price</p>
                  <p className="font-medium">{formatCurrency(SUBSCRIPTION_PRICE.monthly, SUBSCRIPTION_PRICE.currency)}</p>
                </div>
              </div>
            </div>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
                <div>
                  <p className={`text-sm ${styles.infoText}`}>
                    {subscription?.status === 'active' ? 'Next Billing Date' : 
                     subscription?.status === 'trialing' ? 'Trial End Date' : 
                     subscription?.status === 'canceled' ? 'Access Until' : 'Status'}
                  </p>
                  <p className="font-medium">
                    {subscription?.status === 'active' ? formatDate(subscription.currentPeriodEnd) : 
                     subscription?.status === 'trialing' ? formatDate(subscription.trialEnd) : 
                     subscription?.status === 'canceled' ? formatDate(subscription.currentPeriodEnd) : 
                     subscription?.status || 'Not subscribed'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Subscribe button - show if trial or no subscription */}
            {(['trialing', 'canceled', undefined].includes(subscription?.status) ||
              (!subscription?.isActive && !subscription?.daysRemaining)) && (
              <button 
                className={styles.primaryButton + ' flex-1'}
                onClick={handleStartSubscription}
                disabled={processingAction}
              >
                {processingAction ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    <span>Subscribe Now</span>
                  </>
                )}
              </button>
            )}

            {/* Update Payment Method - show if active subscription */}
            {subscription?.status === 'active' && (
              <button 
                className={styles.primaryButton + ' flex-1'}
                onClick={handleUpdatePayment}
                disabled={processingAction}
              >
                {processingAction ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    <span>Manage Payment Method</span>
                  </>
                )}
              </button>
            )}

            {/* Cancel Subscription - show if active */}
            {subscription?.status === 'active' && !showConfirmCancel && (
              <button 
                className={styles.secondaryButton}
                onClick={() => setShowConfirmCancel(true)}
                disabled={processingAction}
              >
                <span>Cancel Subscription</span>
              </button>
            )}

            {/* Reactivate Subscription - show if canceled */}
            {subscription?.status === 'canceled' && (
              <button 
                className={styles.secondaryButton + ' flex-1'}
                onClick={handleReactivateSubscription}
                disabled={processingAction}
              >
                {processingAction ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>Reactivate Subscription</span>
                )}
              </button>
            )}
          </div>

          {/* Confirmation for cancellation */}
          {showConfirmCancel && (
            <div className={`mt-6 p-4 rounded-xl border ${isDarkMode ? 'border-red-500/30 bg-red-500/10' : 'border-red-100 bg-red-50'}`}>
              <p className={`font-medium mb-3 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                Are you sure you want to cancel your subscription?
              </p>
              <p className={`mb-4 text-sm ${isDarkMode ? 'text-red-300/80' : 'text-red-600'}`}>
                You'll continue to have access until the end of your current billing period on {formatDate(subscription?.currentPeriodEnd)}.
              </p>
              <div className="flex gap-3">
                <button 
                  className={styles.dangerButton}
                  onClick={handleCancelSubscription}
                  disabled={processingAction}
                >
                  {processingAction ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <span>Yes, Cancel Subscription</span>
                  )}
                </button>
                <button 
                  className={styles.secondaryButton}
                  onClick={() => setShowConfirmCancel(false)}
                  disabled={processingAction}
                >
                  <span>No, Keep Subscription</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Benefits Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>TV App Benefits</h2>
        </div>
        <div className={styles.cardBody}>
          <ul className="space-y-2 mb-6">
            <li className="flex items-start gap-2">
              <CheckCircle className={`w-5 h-5 mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
              <span>Customizable prayer times display for your TV</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className={`w-5 h-5 mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
              <span>Beautiful background themes and customization options</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className={`w-5 h-5 mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
              <span>Scrolling ticker with custom messages</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className={`w-5 h-5 mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
              <span>Background image slideshow feature</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className={`w-5 h-5 mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
              <span>Easy setup with QR code pairing</span>
            </li>
          </ul>

          <p className={`text-sm ${styles.infoText}`}>
            Your subscription helps us maintain and improve the Ummahti TV app experience. Thank you for your support!
          </p>
        </div>
      </div>

      {/* Trial about to expire or expired */}
      {subscription?.isTrial && subscription?.daysRemaining <= 7 && (
        <div className={styles.card + ' mb-6'}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>
              {subscription.daysRemaining > 0 ? 'Trial Ending Soon' : 'Free Trial Expired'}
            </h2>
          </div>
          <div className={styles.cardBody}>
            <div className={subscription.daysRemaining > 0 ? styles.alert : styles.errorAlert}>
              <span className="mt-0.5">
                {subscription.daysRemaining > 0 ? (
                  <Info className="w-5 h-5" />
                ) : (
                  <AlertTriangle className="w-5 h-5" />
                )}
              </span>
              <div>
                <p className="font-medium">
                  {subscription.daysRemaining > 0 
                    ? `Your free trial ends in ${subscription.daysRemaining} days` 
                    : 'Your free trial has ended'}
                </p>
                <p className="mt-1">
                  {subscription.daysRemaining > 0
                    ? `Your free trial will end on ${formatDate(subscription.trialEnd)}. Subscribe now to continue enjoying premium features.`
                    : 'Subscribe now to regain access to premium features.'}
                </p>
                {subscription.isCustomTrial && (
                  <p className="mt-1 text-sm">
                    <Star className="w-4 h-4 inline mr-1" />
                    This free trial started when you linked your first TV device.
                  </p>
                )}
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className={`text-lg font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Premium TV App Benefits:
              </h3>
              <ul className={`list-disc list-inside ${styles.infoText} space-y-2`}>
                <li>Full access to all TV app features</li>
                <li>Customizable prayer time displays</li>
                <li>Personalized Quran reading plans</li>
                <li>Synchronized settings across all your devices</li>
                <li>HD Islamic wallpapers and themes</li>
              </ul>
            </div>
            
            <div className="mt-6">
              <button
                className={styles.primaryButton + ' w-full py-3 text-lg'}
                onClick={handleStartSubscription}
                disabled={processingAction}
              >
                {processingAction ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    <span>Subscribe Now</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back to Settings button */}
      <div className="mt-6">
        <Link 
          to="/settings/tv" 
          className={styles.secondaryButton + ' inline-flex'}
        >
          Back to TV Settings
        </Link>
      </div>
    </div>
  );
};

export default SubscriptionPage; 